import { BadgeCheck, Clapperboard, FileText, Image as ImageIcon, MonitorPlay, Radio, Scale } from "lucide-react";
import Seo from "@/components/site/Seo";
import EnquiryForm from "@/components/site/EnquiryForm";
import { DemoBadge, EmptyState, Notice, PageHero, Reveal, SectionHeading } from "@/components/site/kit";
import { NewsCard } from "@/components/site/cards";
import useFetch from "@/hooks/useFetch";
import { fileUrl } from "@/lib/api";
import { CONSENT, MEDIA_FIELDS } from "@/lib/forms";

const SECTIONS = [
  { i: Radio, t: "Broadcasting", d: "CGN Sports is exploring broadcasting opportunities to bring the league to television and digital audiences." },
  { i: MonitorPlay, t: "OTT Distribution", d: "Streaming and OTT distribution opportunities are under exploration. No OTT partner has been confirmed." },
  { i: Scale, t: "Media Rights", d: "Enquiries for media rights, highlights and clip licensing are welcome via the media enquiry form." },
  { i: Clapperboard, t: "Production & Distribution", d: "Production partners and distribution collaborators can register interest for live and non-live content." },
  { i: BadgeCheck, t: "Media Accreditation", d: "Accreditation for journalists, photographers and content creators will open ahead of the season." },
];

export default function Media() {
  const { data: releases } = useFetch("/news?category=Media%20Releases", []);
  const { data: gallery } = useFetch("/gallery?scope=media", []);
  return (
    <>
      <Seo title="Media & Broadcasting" description="Broadcasting, OTT, media rights, press releases and media accreditation for the Telangana–Andhra T10 League. Broadcasting partnerships are under exploration." />
      <PageHero testid="media-hero" eyebrow="Media & Broadcasting" title="Taking Regional Cricket to Digital Audiences" image="/images/broadcast.jpg">
        CGN Sports is exploring broadcasting and OTT distribution opportunities for the league.
      </PageHero>
      <section className="container-x py-16">
        <Notice testid="broadcast-notice">Broadcasting partnerships are under exploration. No broadcaster or OTT platform has been confirmed for the league.</Notice>
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {SECTIONS.map(({ i: I, t, d }, n) => (
            <Reveal key={t} delay={n * 0.05}>
              <div className="card-lift h-full rounded-md border border-white/10 bg-[#112240] p-6">
                <I className="h-6 w-6 text-[#00E5FF]" /><h2 className="mt-4 font-display text-2xl uppercase">{t}</h2><p className="mt-2 text-sm text-slate-400">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      <section className="container-x pb-16">
        <SectionHeading eyebrow="Newsroom" title="Press Releases" />
        <div className="mt-8">
          {releases?.length ? <div className="grid gap-6 md:grid-cols-3">{releases.map((n) => <NewsCard key={n.id} article={n} />)}</div>
            : <EmptyState testid="press-empty" icon={FileText} title="No press releases yet">Official press releases will be published here.</EmptyState>}
        </div>
      </section>
      <section className="container-x pb-20">
        <SectionHeading eyebrow="Gallery" title="Photo & Video" />
        <div className="mt-8">
          {gallery?.length ? (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {gallery.map((g) => (
                <figure key={g.id} className="relative overflow-hidden rounded-md">
                  {g.kind === "video" && g.video_url ? <a href={g.video_url} target="_blank" rel="noreferrer"><img src={fileUrl(g.image)} alt={g.title} loading="lazy" className="aspect-square w-full object-cover" /></a>
                    : <img src={fileUrl(g.image)} alt={g.title} loading="lazy" className="aspect-square w-full object-cover" />}
                  {g.is_demo && <span className="absolute left-2 top-2"><DemoBadge /></span>}
                  <figcaption className="absolute inset-x-0 bottom-0 bg-[#0A192F]/80 p-2 text-xs">{g.title}</figcaption>
                </figure>
              ))}
            </div>
          ) : <EmptyState testid="media-gallery-empty" icon={ImageIcon} title="Gallery coming soon" />}
        </div>
      </section>
      <section id="enquiry" className="scroll-mt-28 bg-[#071325] py-20">
        <div className="container-x grid gap-12 lg:grid-cols-[1fr_1.3fr]">
          <SectionHeading eyebrow="Get in Touch" title="Media & Broadcasting Enquiry">For broadcasting, OTT, media rights, production, press and accreditation enquiries.</SectionHeading>
          <EnquiryForm testid="media-form" fields={MEDIA_FIELDS} endpoint="/forms/media" submitLabel="Send Media Enquiry" consentText={CONSENT}
            success="Thank you for your media enquiry. The league media team will respond shortly." />
        </div>
      </section>
    </>
  );
}
